import { supabase } from './supabase';

// Mirrors is_household_premium() in migration 018 (active/trialing, period
// covers now, +1h grace). UI state only — real gates are RLS/Edge Functions.
export function isPremium(subscription) {
  if (!subscription) return false;
  if (!['active', 'trialing'].includes(subscription.status)) return false;
  if (!subscription.current_period_end) return false;
  return new Date(subscription.current_period_end).getTime() + 60 * 60 * 1000 > Date.now();
}

// Free-tier limits, mirrored from the database gates (migration 019) for
// messaging only — the database values are the source of truth.
export const FREE_RECIPE_LIMIT = 10;
export const FREE_HISTORY_DAYS = 28;

/**
 * Fetch live Premium prices from Stripe (via get-prices Edge Function).
 * @returns {Promise<{monthly?: {amount, currency}, annual?: {amount, currency}, error?: string}>}
 */
export async function fetchPrices() {
  try {
    const { data, error } = await supabase.functions.invoke('get-prices');
    if (error) return { error: error.message };
    return data || {};
  } catch (err) {
    return { error: err.message };
  }
}

/** Format a Stripe amount (cents) for display, e.g. 499 → "$4.99". */
export function formatPrice(price) {
  if (!price || price.amount == null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (price.currency || 'usd').toUpperCase(),
  }).format(price.amount / 100);
}

/**
 * Start a Stripe Checkout for the household subscription and redirect to the
 * Stripe-hosted payment page. Server enforces that only adults can purchase.
 *
 * @param {'monthly'|'annual'} plan
 * @returns {Promise<{error?: string}>} only returns on failure — on success
 *   the browser navigates away to Stripe.
 */
export async function startCheckout(plan) {
  return redirectToBillingUrl('create-checkout-session', { plan });
}

/**
 * Open the Stripe Customer Portal (manage/cancel/update card) and redirect.
 * Server enforces adults-only and that a real Stripe subscription exists.
 *
 * @returns {Promise<{error?: string}>} only returns on failure — on success
 *   the browser navigates away to Stripe.
 */
export async function openBillingPortal() {
  return redirectToBillingUrl('create-portal-session', {});
}

async function redirectToBillingUrl(functionName, body) {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, { body });
    if (error) {
      // functions.invoke wraps non-2xx in a generic error; the JSON body
      // (e.g. code: 'already_subscribed') is on error.context
      const errBody = await error.context?.json?.().catch(() => null);
      return { error: errBody?.error || error.message };
    }
    if (!data?.url) return { error: 'No redirect URL returned' };
    window.location.assign(data.url);
    return {};
  } catch (err) {
    return { error: err.message };
  }
}
